/**
 * Notification Types
 * TypeScript interfaces for the notification system
 */

export enum NotificationCategory {
    SYSTEM = "SYSTEM",
    ACHIEVEMENT = "ACHIEVEMENT",
    QUIZ = "QUIZ",
    PRACTICE = "PRACTICE",
    COURSE = "COURSE",
    SOCIAL = "SOCIAL",
    PAYMENT = "PAYMENT",
    REMINDER = "REMINDER",
}

export enum NotificationPriority {
    LOW = "LOW",
    NORMAL = "NORMAL",
    HIGH = "HIGH",
    URGENT = "URGENT",
}

export enum NotificationType {
    SYSTEM = "SYSTEM",
    ACHIEVEMENT = "ACHIEVEMENT",
    QUIZ = "QUIZ",
    PRACTICE = "PRACTICE",
    COURSE = "COURSE",
    SOCIAL = "SOCIAL",
    PAYMENT = "PAYMENT",
    REMINDER = "REMINDER",
}

export enum DevicePlatform {
    IOS = "IOS",
    ANDROID = "ANDROID",
    WEB = "WEB",
}

export interface Notification {
    id: string;
    user_id: number;
    template_id?: number;
    type: NotificationType;
    category: NotificationCategory;
    priority: NotificationPriority;
    title: string;
    message: string;
    channels: string[];
    delivery_status: Record<string, string>;
    is_read: boolean;
    read_at?: string;
    clicked: boolean;
    clicked_at?: string;
    action_url?: string;
    resource_type?: string;
    resource_id?: string;
    template_data?: Record<string, any>;
    expires_at?: string;
    extra_metadata?: Record<string, any>;
    created_at: string;
    updated_at: string;
}

export interface NotificationList {
    items: Notification[];
    total: number;
    page: number;
    limit: number;
    has_more: boolean;
}

export interface UnreadCount {
    total: number;
    by_category: Record<string, number>;
}

export interface NotificationPreference {
    id: number;
    user_id: number;
    enabled: boolean;
    quiet_hours_start?: string;
    quiet_hours_end?: string;
    category_preferences: Record<string, Record<string, boolean>>;
    max_emails_per_day: number;
    max_push_per_hour: number;
    auto_delete_read_after_days: number;
    delivery_counts: Record<string, number>;
    created_at: string;
    updated_at: string;
}

export interface NotificationDevice {
    id: number;
    user_id: number;
    device_token: string;
    platform: DevicePlatform;
    device_name?: string;
    device_model?: string;
    app_version?: string;
    is_active: boolean;
    last_used_at?: string;
    created_at: string;
}

export interface NotificationQuery {
    category?: NotificationCategory;
    is_read?: boolean;
    priority?: NotificationPriority;
    type?: NotificationType;
    page?: number;
    limit?: number;
    sort?: "newest" | "oldest" | "priority";
}

export interface NotificationPreferenceUpdate {
    enabled?: boolean;
    quiet_hours_start?: string;
    quiet_hours_end?: string;
    category_preferences?: Record<string, Record<string, boolean>>;
    max_emails_per_day?: number;
    max_push_per_hour?: number;
    auto_delete_read_after_days?: number;
}

export interface DeviceRegistration {
    device_token: string;
    platform: DevicePlatform;
    device_name?: string;
    device_model?: string;
    app_version?: string;
}

export interface BulkMarkReadRequest {
    notification_ids?: string[];
    category?: NotificationCategory;
}

// WebSocket message types
export interface WebSocketMessage {
    type: "notification" | "unread_count" | "connected" | "pong" | "error";
    data: any;
}

export interface NotificationWebSocketMessage extends WebSocketMessage {
    type: "notification";
    data: Notification;
}

export interface UnreadCountWebSocketMessage extends WebSocketMessage {
    type: "unread_count";
    data: UnreadCount;
}
